/** @file jointFracsOf: this frame's per-node pivot fractions of a drawn preview puppet. */
import type { Profile } from '@zilo/pose3d-format/types/profile';
import { jointFracs } from './jointFracs';
import type { PreviewPuppet } from './previewPuppet';

/** Per preview puppet: node index by drawable index and the fraction buffer, made on its first frame. */
type Scratch = { nodeOf: (drawable: number) => number; buf: Float64Array };
const scratch = new WeakMap<PreviewPuppet, Scratch>();

/** The scratch of `p`: `drawables[d].node` as an index in `p.names`. */
function scratchOf(p: PreviewPuppet): Scratch {
  const hit = scratch.get(p);
  if (hit !== undefined) return hit;
  const nodes = p.bundle.puppet.drawables.map((d) => p.names.indexOf(d.node));
  const made: Scratch = { nodeOf: (d) => nodes[d] ?? -1, buf: new Float64Array(2 * p.names.length) };
  scratch.set(p, made);
  return made;
}

/**
 * `jointFracs` over `p`'s placed impostors this frame; nodes that draw nothing get 0.5 with `profile.halfPixel`
 * (a point marks the pixel it falls in, as the server's joint overlay does), else 0 (the nearest pixel corner).
 */
export function jointFracsOf(p: PreviewPuppet, profile: Profile): Float64Array {
  const s = scratchOf(p);
  const { list, count } = p.instance.state();
  return jointFracs(list, count, s.nodeOf, p.names.length, profile.halfPixel ? 0.5 : 0, s.buf);
}
